import * as XLSX from 'xlsx';

/**
 * 扩展 SheetJS worksheet 的 !ref，使 sheet_to_html 包含合并区、!cols、稀疏格所在的最右/最下列，
 * 避免表格右侧缺列、外框竖线丢失。
 */
export function expandWorksheetRange(worksheet) {
  if (!worksheet['!ref']) {
    worksheet['!ref'] = 'A1';
  }
  const base = XLSX.utils.decode_range(worksheet['!ref']);
  let maxR = base.e.r;
  let maxC = base.e.c;

  if (worksheet['!merges']) {
    worksheet['!merges'].forEach((m) => {
      if (m.e.r > maxR) maxR = m.e.r;
      if (m.e.c > maxC) maxC = m.e.c;
    });
  }
  Object.keys(worksheet).forEach((k) => {
    if (k[0] === '!') return;
    try {
      const addr = XLSX.utils.decode_cell(k);
      const cell = worksheet[k];
      const v = cell && (cell.v ?? cell.w);
      if (
        v !== undefined &&
        v !== null &&
        String(v).trim() !== '' &&
        addr.r > maxR
      ) {
        maxR = addr.r;
      }
      if (
        v !== undefined &&
        v !== null &&
        String(v).trim() !== '' &&
        addr.c > maxC
      ) {
        maxC = addr.c;
      }
    } catch (e) {
      /* ignore */
    }
  });

  worksheet['!ref'] = XLSX.utils.encode_range({
    s: { r: base.s.r, c: base.s.c },
    e: { r: maxR, c: maxC },
  });
}

/** 去掉 !ref 末尾无内容的空行/空列，避免 sheet_to_html 多出一行只剩右边框 */
export function shrinkWorksheetRangeToContent(worksheet) {
  if (!worksheet['!ref']) return;
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  let maxR = range.s.r;
  let maxC = range.s.c;

  const cellHasContent = (r, c) => {
    const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
    if (!cell) return false;
    const v = cell.v ?? cell.w;
    if (v === undefined || v === null) return false;
    return String(v).trim() !== '';
  };

  Object.keys(worksheet).forEach((k) => {
    if (k[0] === '!') return;
    try {
      const addr = XLSX.utils.decode_cell(k);
      if (cellHasContent(addr.r, addr.c)) {
        if (addr.r > maxR) maxR = addr.r;
        if (addr.c > maxC) maxC = addr.c;
      }
    } catch (e) {
      /* ignore */
    }
  });

  if (worksheet['!merges']) {
    worksheet['!merges'].forEach((m) => {
      let mergeHasContent = false;
      for (let r = m.s.r; r <= m.e.r && !mergeHasContent; r++) {
        for (let c = m.s.c; c <= m.e.c; c++) {
          if (cellHasContent(r, c)) {
            mergeHasContent = true;
            break;
          }
        }
      }
      if (mergeHasContent) {
        if (m.e.r > maxR) maxR = m.e.r;
        if (m.e.c > maxC) maxC = m.e.c;
      }
    });
  }

  worksheet['!ref'] = XLSX.utils.encode_range({
    s: { r: range.s.r, c: range.s.c },
    e: { r: maxR, c: maxC },
  });
}

/**
 * 合并富文本多段 Run，避免 sheet_to_html 只取到短串。
 * 说明：不少模板「日期横线」是空格+下划线格式（&lt;u/&gt;），不是字符 _；与纯占位符串不同。
 */
export function normalizeCellsForSheetHtml(worksheet) {
  Object.keys(worksheet).forEach((k) => {
    if (k[0] === '!') return;
    const cell = worksheet[k];
    if (!cell) return;

    if (cell.r && Array.isArray(cell.r)) {
      const full = cell.r.map((part) => (part.t || '').toString()).join('');
      if (full.length > 0) {
        cell.v = full;
        cell.w = full;
        cell.t = 'str';
      }
      return;
    }

    if (typeof cell.r === 'string' && cell.r.includes('<t')) {
      const parts = [];
      const re = /<t[^>]*>([^<]*)<\/t>/g;
      let m;
      while ((m = re.exec(cell.r)) !== null) {
        parts.push(
          m[1]
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
        );
      }
      const full = parts.join('');
      if (full.length > 0) {
        cell.v = full;
        cell.w = full;
        cell.t = 'str';
      }
    }
  });
}

/** 列宽数组求和（px），用于固定表格总宽，避免 width:100% 拉伸导致右下角边框错位 */
export function sumColWidths(colWidths) {
  return (colWidths || []).reduce((sum, w) => sum + (Number(w) || 0), 0);
}

const BORDER_WIDTH_MAP = {
  THIN: '1px',
  MEDIUM: '2px',
  THICK: '3px',
};

const BORDER_PRIORITY = { NONE: 0, THIN: 1, MEDIUM: 2, THICK: 3 };

function normalizeColor(color) {
  if (!color) return '#000000';
  return color.startsWith('#') ? color : `#${color}`;
}

function pickStrongerBorder(current, type, color) {
  if (!type || type === 'NONE') return current;
  const nextPri = BORDER_PRIORITY[type] ?? 1;
  const curPri = current ? (BORDER_PRIORITY[current.type] ?? 0) : 0;
  if (nextPri >= curPri) {
    return { type, color: normalizeColor(color) };
  }
  return current;
}

function attrToCss(attr) {
  const style = [];
  if (attr.fontSize) style.push(`font-size: ${attr.fontSize}px`);
  if (attr.backgroundColor) {
    style.push(`background-color: ${attr.backgroundColor}`);
  }
  if (attr.fontColor) {
    style.push(`color: ${normalizeColor(attr.fontColor)} !important`);
  }
  if (attr.bold) style.push('font-weight: bold');
  if (attr.horizontalAlign) {
    style.push(`text-align: ${attr.horizontalAlign.toLowerCase()}`);
  }
  if (attr.verticalAlign) {
    style.push(`vertical-align: ${attr.verticalAlign.toLowerCase()}`);
  }

  const sides = {
    top: { type: attr.topBorder, color: attr.topColor },
    bottom: { type: attr.bottomBorder, color: attr.bottomColor },
    left: { type: attr.leftBorder, color: attr.leftColor },
    right: { type: attr.rightBorder, color: attr.rightColor },
  };
  Object.entries(sides).forEach(([direction, { type, color }]) => {
    if (!type) return;
    if (type === 'NONE') {
      style.push(`border-${direction}: none !important`);
      return;
    }
    const width = BORDER_WIDTH_MAP[type] || '1px';
    style.push(
      `border-${direction}: ${width} solid ${normalizeColor(color)} !important`
    );
  });

  return style.join('; ');
}

/**
 * 将 getCellAttribute 结果转为 applyStylesToHtml 可用的样式表。
 * 合并区内边框汇总到左上角（与 sheet_to_html 的 colspan/rowspan td 一致）。
 */
export function buildStylesFromCellAttributes(cellAttributes, worksheet) {
  const attrMap = {};
  (cellAttributes || []).forEach((attr) => {
    if (attr && attr.cellAddress) {
      attrMap[attr.cellAddress.toUpperCase()] = { ...attr };
    }
  });

  const skip = new Set();
  const merges = (worksheet && worksheet['!merges']) || [];

  merges.forEach((m) => {
    for (let r = m.s.r; r <= m.e.r; r++) {
      for (let c = m.s.c; c <= m.e.c; c++) {
        if (r !== m.s.r || c !== m.s.c) {
          skip.add(XLSX.utils.encode_cell({ r, c }).toUpperCase());
        }
      }
    }

    const anchor = XLSX.utils.encode_cell({ r: m.s.r, c: m.s.c }).toUpperCase();
    const base = attrMap[anchor]
      ? { ...attrMap[anchor] }
      : { cellAddress: anchor };

    let top;
    let bottom;
    let left;
    let right;
    for (let r = m.s.r; r <= m.e.r; r++) {
      for (let c = m.s.c; c <= m.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c }).toUpperCase();
        const attr = attrMap[addr];
        if (!attr) continue;
        if (r === m.s.r) {
          top = pickStrongerBorder(top, attr.topBorder, attr.topColor);
        }
        if (r === m.e.r) {
          bottom = pickStrongerBorder(bottom, attr.bottomBorder, attr.bottomColor);
        }
        if (c === m.s.c) {
          left = pickStrongerBorder(left, attr.leftBorder, attr.leftColor);
        }
        if (c === m.e.c) {
          right = pickStrongerBorder(right, attr.rightBorder, attr.rightColor);
        }
      }
    }

    if (top) {
      base.topBorder = top.type;
      base.topColor = top.color;
    } else {
      base.topBorder = 'NONE';
    }
    if (bottom) {
      base.bottomBorder = bottom.type;
      base.bottomColor = bottom.color;
    } else {
      base.bottomBorder = 'NONE';
    }
    if (left) {
      base.leftBorder = left.type;
      base.leftColor = left.color;
    } else {
      base.leftBorder = 'NONE';
    }
    if (right) {
      base.rightBorder = right.type;
      base.rightColor = right.color;
    } else {
      base.rightBorder = 'NONE';
    }

    attrMap[anchor] = base;
  });

  const styles = {};
  Object.keys(attrMap).forEach((addr) => {
    if (skip.has(addr)) return;
    const css = attrToCss(attrMap[addr]);
    if (css) styles[addr] = css;
  });
  return styles;
}

/** sheet_to_html 结果注入列宽、行高与单元格样式，表格使用固定总宽 */
export function applyStylesToHtml(html, styles) {
  const totalWidth = styles.totalWidth || sumColWidths(styles.colWidths);
  const colgroup = `<colgroup>${(styles.colWidths || [])
    .map((w) => `<col style="width: ${w}px">`)
    .join('')}</colgroup>`;
  html = html.replace(
    /<table[^>]*>/,
    `<table style="width:${totalWidth}px;table-layout:fixed;border-collapse:collapse;height:auto;">${colgroup}`
  );

  let rowIndex = 0;
  html = html.replace(/<tr[^>]*>/g, (match) => {
    const height = styles.rowHeights[rowIndex++];
    return height
      ? match.replace('>', ` style="height: ${height}px">`)
      : match;
  });

  return html.replace(/<td( [^>]*)?>/g, (match, attributes) => {
    const cellAddressMatch =
      attributes &&
      (attributes.match(/id="[^"]*?([A-Za-z]+\d+)"/) ||
        attributes.match(/data-cell="([A-Za-z]+\d+)"/));

    const existingStyle =
      (attributes && (attributes.match(/style="([^"]*)"/) || [])[1]) || '';
    const strippedExisting = existingStyle
      .replace(/\bborder(-[a-z]+)?\s*:[^;]+;?/gi, '')
      .replace(/;{2,}/g, ';')
      .replace(/^;|;$/g, '')
      .trim();

    if (cellAddressMatch && cellAddressMatch[1]) {
      const cellAddress = cellAddressMatch[1].toUpperCase();
      if (styles[cellAddress]) {
        const mergedStyle = [strippedExisting, styles[cellAddress]]
          .filter(Boolean)
          .join('; ')
          .replace(/;{2,}/g, ';')
          .replace(/^;|;$/g, '');
        return `<td style="${mergedStyle}"${attributes.replace(
          /style="[^"]*"/,
          ''
        )}>`;
      }
    }

    if (strippedExisting !== existingStyle) {
      const attrsWithoutStyle = (attributes || '').replace(
        /style="[^"]*"/,
        ''
      );
      if (strippedExisting) {
        return `<td style="${strippedExisting}"${attrsWithoutStyle}>`;
      }
      return `<td${attrsWithoutStyle || ''}>`;
    }

    return match;
  });
}
